import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { categoryLinks, mainSiteUrl } from "@/pages/home/data/categories";

export default function Navbar() {
  const { t } = useTranslation();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const mobileRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (mobileRef.current && !mobileRef.current.contains(e.target as Node)) {
        setMobileOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const closeAll = () => {
    setMobileOpen(false);
    setDropdownOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer"
            onClick={closeAll}
          >
            <span className={`text-lg md:text-xl font-heading font-semibold tracking-tight whitespace-nowrap transition-colors duration-300 ${scrolled ? "text-foreground-900" : "text-background-50"}`}>
              Itaobuy <span className={scrolled ? "text-primary-500" : "text-primary-300"}>Spreadsheet</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer font-medium ${scrolled ? "text-foreground-700 hover:text-primary-500" : "text-background-50/90 hover:text-background-50"}`}
            >
              {t("nav.home")}
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${scrolled ? "text-foreground-700 hover:text-primary-500" : "text-background-50/90 hover:text-background-50"}`}
              >
                {t("nav.categories")}
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${
                    dropdownOpen ? "rotate-180" : ""
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200 shadow-lg py-2 z-50 max-h-80 overflow-y-auto">
                  {categoryLinks.map((cat) => (
                    <a
                      key={cat.key}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <div className="w-7 h-7 flex-shrink-0 rounded overflow-hidden">
                        <img src={cat.image} alt={t(cat.labelKey)} className="w-full h-full object-cover object-top" />
                      </div>
                      {t(cat.labelKey)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`flex items-center gap-1.5 text-sm transition-colors whitespace-nowrap cursor-pointer ${scrolled ? "text-foreground-700 hover:text-primary-500" : "text-background-50/90 hover:text-background-50"}`}
            >
              <i className="ri-shopping-bag-3-line"></i>
              {t("nav.shoppingGuide")}
            </a>

            <a
              href="#faq"
              className={`text-sm transition-colors whitespace-nowrap cursor-pointer ${scrolled ? "text-foreground-700 hover:text-primary-500" : "text-background-50/90 hover:text-background-50"}`}
            >
              {t("nav.faq")}
            </a>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`px-5 py-2 text-sm font-medium rounded-full transition-colors whitespace-nowrap cursor-pointer ${scrolled ? "bg-primary-500 text-background-50 hover:bg-primary-600" : "bg-background-50 text-primary-600 hover:bg-background-100"}`}
            >
              {t("hero.cta_primary")}
            </a>
          </div>

          <button
            className="md:hidden w-9 h-9 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`text-xl transition-colors duration-300 ${scrolled ? "text-foreground-800" : "text-background-50"} ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div ref={mobileRef} className="md:hidden bg-background-50 border-t border-background-200 px-4 py-3 max-h-[70vh] overflow-y-auto">
          <Link
            to="/"
            className="flex items-center gap-2 py-3 text-sm font-medium text-foreground-800 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            onClick={closeAll}
          >
            <i className="ri-home-4-line"></i>
            {t("nav.home")}
          </Link>

          <div className="border-t border-background-200 my-2"></div>

          <p className="py-2 text-xs text-foreground-400 uppercase tracking-wider font-semibold flex items-center gap-2">
            <i className="ri-grid-line"></i>
            {t("nav.categories")}
          </p>

          <div className="grid grid-cols-2 gap-1 mb-2">
            {categoryLinks.map((cat) => (
              <a
                key={cat.key}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-2 py-2 px-2 text-xs text-foreground-600 hover:text-primary-500 hover:bg-background-100 rounded-md transition-colors cursor-pointer"
                onClick={closeAll}
              >
                <div className="w-8 h-8 flex-shrink-0 rounded overflow-hidden">
                  <img src={cat.image} alt={t(cat.labelKey)} className="w-full h-full object-cover object-top" />
                </div>
                <span className="leading-tight">{t(cat.labelKey)}</span>
              </a>
            ))}
          </div>

          <div className="border-t border-background-200 my-2"></div>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="flex items-center gap-2 py-3 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            onClick={closeAll}
          >
            <i className="ri-shopping-bag-3-line"></i>
            {t("nav.shoppingGuide")}
          </a>

          <a
            href="#faq"
            className="flex items-center gap-2 py-3 text-sm text-foreground-700 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
            onClick={closeAll}
          >
            <i className="ri-question-line"></i>
            {t("nav.faq")}
          </a>

          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            className="block mt-3 text-center px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            {t("hero.cta_primary")}
          </a>
        </div>
      )}
    </nav>
  );
}